import { useState } from "react";
import { useApp } from "../App";
import api from "../services/api";
import "../styles/TrackOrderPage.css";

const STEPS = ["Order Placed", "Confirmed", "Shipped", "Out for Delivery", "Delivered"];

export default function TrackOrderPage() {
  const { showToast } = useApp();
  const [orderId,  setOrderId]  = useState("");
  const [email,    setEmail]    = useState("");
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) { showToast("Please enter an order number.", "error"); return; }
    setLoading(true);
    try {
      const data = await api.orders.track(orderId.trim());
      setResult(data);
    } catch (err) {
      showToast(err.message || "Order not found.", "error");
      setResult(null);
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="track-hero">
        <div className="container">
          <h1>Track Your Order</h1>
          <p>Enter your order number to get real-time updates</p>
        </div>
      </div>

      <div className="container">
        <div className="track-form-card">
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, color: "var(--primary)", marginBottom: 20 }}>Enter Order Details</h2>
          <form onSubmit={handleTrack}>
            <div className="form-group">
              <label className="form-label">Order Number *</label>
              <input className="form-control" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder="e.g. ND-2026-001" required />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address (optional)</label>
              <input className="form-control" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" />
            </div>
            <button className="btn btn-primary btn-full topbtn" type="submit" style={{ padding: 16 }} disabled={loading}>
              {loading ? "Tracking…" : "Track Order"}
            </button>
          </form>
          <p style={{ fontSize: 12, color: "var(--text-light)", marginTop: 14, textAlign: "center" }}>
            Your order number was emailed to you after placing the order.
          </p>
        </div>

        {result && (
          <div style={{ background: "var(--white)", borderRadius: "var(--radius-md)", padding: 32, boxShadow: "var(--shadow-md)", marginBottom: 64 }}>
            {/* Order Meta */}
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 28, paddingBottom: 20, borderBottom: "2px solid var(--border)" }}>
              {[
                { label: "Order Number", val: result.id },
                { label: "Order Date",   val: result.date },
                { label: "Items",        val: `${result.items} item(s)` },
                { label: "Total",        val: `₹${result.total?.toLocaleString()}` },
              ].map(({ label, val }) => (
                <div key={label}>
                  <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "var(--gold)", marginBottom: 4 }}>{label}</p>
                  <p style={{ fontWeight: 700, fontFamily: "var(--font-display)", fontSize: label === "Order Number" ? 22 : 16, color: "var(--primary)" }}>{val}</p>
                </div>
              ))}
            </div>

            {/* Product */}
            <div style={{ background: "var(--cream)", borderRadius: "var(--radius-md)", padding: "16px 20px", marginBottom: 28 }}>
              <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "var(--gold)", marginBottom: 6 }}>Product</p>
              <p style={{ fontWeight: 700, color: "var(--text-dark)" }}>{result.product}</p>
              {result.trackingNumber && (
                <p style={{ fontSize: 13, color: "var(--text-mid)", marginTop: 4 }}>
                  Tracking: <strong>{result.trackingNumber}</strong>
                  {result.courierPartner && ` via ${result.courierPartner}`}
                </p>
              )}
            </div>

            {/* Steps */}
            <div style={{ marginBottom: 32 }}>
              <div className="track-steps">
                {STEPS.map((label, i) => {
                  const done    = i < result.status;
                  const current = i === result.status - 1;
                  return (
                    <div key={label} className={`track-step ${done ? "completed" : ""} ${current ? "current" : ""}`}>
                      <div className="track-step-dot">{done ? "✓" : current ? "●" : i + 1}</div>
                      <div className="track-step-label">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Timeline */}
            <h3 style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "var(--primary)", marginBottom: 16 }}>Tracking History</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {result.updates?.map((u, i) => (
                <div key={i} style={{ display: "flex", gap: 16, paddingBottom: 20, position: "relative" }}>
                  {i < result.updates.length - 1 && (
                    <div style={{ position: "absolute", left: 15, top: 32, width: 2, height: "calc(100% - 8px)", background: u.done ? "var(--primary)" : "var(--border)" }} />
                  )}
                  <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: u.done ? "var(--primary)" : "var(--border)", display: "flex", alignItems: "center", justifyContent: "center", color: u.done ? "#fff" : "var(--text-light)", fontSize: 14, fontWeight: 700, position: "relative", zIndex: 1 }}>
                    {u.done ? "✓" : "○"}
                  </div>
                  <div style={{ paddingTop: 4 }}>
                    <p style={{ fontWeight: 700, color: u.done ? "var(--text-dark)" : "var(--text-light)", marginBottom: 2 }}>{u.msg}</p>
                    <p style={{ fontSize: 12, color: "var(--text-light)" }}>{u.date}{u.time && u.time !== "—" ? ` · ${u.time}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16, padding: "14px 18px", background: "var(--cream)", borderRadius: "var(--radius-md)", fontSize: 13, color: "var(--text-mid)" }}>
              <strong style={{ color: "var(--text-dark)" }}>📍 Delivery Address: </strong>{result.address}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
